import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ContentFlags } from "@/components/admin/ContentFlags";
import { api, errMsg, imgUrl, timeAgo } from "@/lib/api";
import { Empty, PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ContentBlocks } from "@/pages/admin/ContentBlocks";

const targetLabel = (r) => r.target?.title || r.target?.headline || r.target?.text || r.target?.name || "(no longer available)";

export default function AdminModeration() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/moderation").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;
  const act = async (url, action) => { try { await api.post(url, { action }); toast.success({ remove: "Removed", restore_media: "Media restored" }[action] || "Dismissed"); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-10" data-testid="admin-moderation-page">
      <PageHeader env="admin" title="Moderation queue" subtitle="User reports across Feed and Facts, plus content flagged by automated checks." />
      <ContentFlags />
      <section className="space-y-3">
        <h2 className="text-2xl">Open reports ({data.reports.length})</h2>
        {data.reports.length === 0 ? <Empty title="No open reports" /> : data.reports.map((r) => (
          <div key={r.id} className="surface p-4" data-testid={`report-${r.id}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{r.target_type} · {r.reason}</p>
            <p className="mt-1 line-clamp-2 text-sm text-slate-800">{targetLabel(r)}</p>
            {r.details && <p className="mt-1 text-xs text-slate-500">“{r.details}”</p>}
            <p className="mt-1 text-xs text-slate-400">{r.reporter_id === "middle-ai" ? "Held by Middle's automatic media screening" : `Reported by ${r.reporter?.name}`} · {timeAgo(r.created_at)}</p>
            {r.target_type === "post" && r.target?.media_status === "blocked" && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid={`report-media-${r.id}`}>
                {(r.target.images || []).map((u) => <img key={u} src={imgUrl(u)} alt="Held media" className="h-24 w-24 rounded-lg object-cover" />)}
                {r.target.video && <video src={imgUrl(r.target.video)} controls playsInline preload="metadata" className="h-32 rounded-lg bg-black" />}
              </div>)}
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" data-testid={`dismiss-report-${r.id}`} onClick={() => act(`/admin/reports/${r.id}`, "dismiss")}>Dismiss</Button>
              {r.target_type === "post" && r.target?.media_status === "blocked" && <Button size="sm" data-testid={`restore-media-report-${r.id}`} onClick={() => act(`/admin/reports/${r.id}`, "restore_media")}>Restore media</Button>}
              {r.target_type !== "user" && r.target_type !== "group" && <Button size="sm" variant="destructive" data-testid={`remove-report-${r.id}`} onClick={() => act(`/admin/reports/${r.id}`, "remove")}>Remove content</Button>}
            </div>
          </div>
        ))}
      </section>
      <ContentBlocks />
    </div>
  );
}
